import { Directive, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';

/**
 * Hides elements with `aria-hidden="true"` when element is less than 50% in the visible viewport area,
 * and restores their original `aria-hidden` state when at least 50% visible.
 * The visible area excludes the `ion-footer` height and adjusts dynamically if it changes.
 */
@Directive({
    selector: '[ridangoAriaVisibilityObserver]'
})
export class AriaVisibilityObserverDirective implements OnInit, OnDestroy {
    private intersectionObserver: IntersectionObserver;
    private mutationObserver: MutationObserver;
    private resizeObserver: ResizeObserver;
    private originalAriaHiddenMap = new WeakMap<Element, string | null>();
    private observedElements = new Set<Element>();
    private footerHeight = 0;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2
    ) {}

    ngOnInit() {
        const footer = document.querySelector('ion-footer') as HTMLElement;

        if (footer) {
            this.footerHeight = footer.offsetHeight || 0;
            this.observeFooterResize(footer);
        }

        this.setupIntersectionObserver();
        this.observeInitialElements();
        this.observeContentMutations();
    }

    private setupIntersectionObserver() {
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }

        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const target = entry.target as Element;

                    // Element needs to be more than 50% visible to restore aria-hidden
                    if (entry.intersectionRatio >= 0.5) {
                        let el: Element | null = target;
                        const maxDepth = 20;
                        let depth = 0;

                        while (el && el !== this.el.nativeElement.parentElement && depth++ < maxDepth) {
                            const original = this.originalAriaHiddenMap.get(el);
                            if (original === null) {
                                this.renderer.removeAttribute(el, 'aria-hidden');
                            } else if (original !== undefined) {
                                this.renderer.setAttribute(el, 'aria-hidden', original);
                            }
                            el = el.parentElement;
                        }

                        if (depth >= maxDepth) {
                            console.warn(
                                'Max DOM depth reached while restoring aria-hidden chain, this should not happen normally. Is Node tree cyclic?'
                            );
                        }
                    } else {
                        this.renderer.setAttribute(target, 'aria-hidden', 'true');
                    }
                });
            },
            {
                // We observe the area above footer excluding the footer itself
                // Element needs to be at least 50% visible in that area, to consider it intersecting
                threshold: [0.5],
                rootMargin: `0px 0px -${this.footerHeight}px 0px`
            }
        );

        this.observedElements.forEach((el) => this.intersectionObserver.observe(el));
    }

    private observeInitialElements() {
        const elements: Element[] = [...this.el.nativeElement.querySelectorAll('*')];
        elements.forEach((el) => {
            if (!this.originalAriaHiddenMap.has(el)) {
                this.originalAriaHiddenMap.set(el, el.getAttribute('aria-hidden'));
                this.intersectionObserver.observe(el);
                this.observedElements.add(el);
            }
        });
    }

    private observeContentMutations() {
        this.mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof Element) {
                        const newElements = [node, ...Array.from(node.querySelectorAll('*'))];
                        newElements.forEach((el) => {
                            if (!this.originalAriaHiddenMap.has(el)) {
                                this.originalAriaHiddenMap.set(el, el.getAttribute('aria-hidden'));
                                this.intersectionObserver.observe(el);
                                this.observedElements.add(el);
                            }
                        });
                    }
                });
            });
        });

        this.mutationObserver.observe(this.el.nativeElement, {
            childList: true,
            subtree: true
        });
    }

    private observeFooterResize(footer: HTMLElement) {
        // It doesn't resize often, but we are waiting for the first height change at least
        this.resizeObserver = new ResizeObserver(() => {
            const newHeight = footer.offsetHeight;
            if (newHeight !== this.footerHeight) {
                this.footerHeight = newHeight;
                this.setupIntersectionObserver();
            }
        });

        this.resizeObserver.observe(footer);
    }

    ngOnDestroy() {
        this.intersectionObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.resizeObserver?.disconnect();
    }
}
